import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from 'react'

import type {
  GetStaticPropsContext,
  GetStaticPropsResult,
  NextPage,
} from 'next'
import { useTranslations } from 'next-intl'

import { indexes } from '@mab-common/utils'
import EsIndexTypes = indexes.EsIndexTypes

import { useRouter } from 'next/router'

import classNames from 'classnames'
import useSWR from 'swr'

import AnimateIntoView, {
  AnimationType,
} from '~/components/atoms/AnimateIntoView/AnimateIntoView'
import Button, {
  ButtonTypes,
  ButtonVariants,
} from '~/components/atoms/Button/Button'
import Container, { gutterTypes } from '~/components/atoms/Container/Container'
import { urlFor } from '~/components/atoms/Image/Image'
import Link from '~/components/atoms/Link/Link'
import Loader from '~/components/atoms/Loader/Loader'
import PortfolioTableArtwork from '~/components/atoms/PortfolioTableArtwork/PortfolioTableArtwork'
import PulsingDot, { DotSize } from '~/components/atoms/PulsingDot/PulsingDot'
import Text, { TextSizes } from '~/components/atoms/Text/Text'
import AuctionHistory from '~/components/blocks/AuctionHistory/AuctionHistory'
import AuthDialog from '~/components/blocks/AuthDialog/AuthDialog'
import GoogleInlineReview from '~/components/blocks/GoogleInlineReview/GoogleInlineReview'
import { ArtworkSpecDetails } from '~/components/blocks/PDPHero/ArtworkSpecDetails'
import { getDimensionString } from '~/components/blocks/PDPHero/PDPHero'
import { resolveBlock } from '~/components/serializers'
import { useUserPortfolio } from '~/containers/AppContainer/PortfolioProvider'
import { useAuthV2 } from '~/contexts/AuthContext'
import { GlobalStoreContext } from '~/contexts/GlobalStoreContext'
import { GlobalStoreProps } from '~/contexts/GlobalStoreReducer'
import { SelectedArtworkContext } from '~/contexts/SelectedArtworkContext'
import { CurrentArtworkProps } from '~/contexts/SelectedArtworkReducer'
import { FormTypes, TicketTypes } from '~/data/forms/shared/interfaces/Form'
import { isValuationType } from '~/data/valuationTypes'
import useAddToPortfolioActions from '~/hooks/usAddToMyPortfolioActions'
import useFormAnalytics from '~/hooks/useFormAnalytics'
import useInstantValuationFormAnalytics from '~/hooks/useInstantValuationFormAnalytics'
import useMabPostHog, {
  PushInteractionType,
  PushWebProduct,
} from '~/hooks/useMabPostHog'
import type { AuctionHistoryItem } from '~/hooks/useRomeoArtworkHistory'
import { useZepic } from '~/hooks/useZepic'
import {
  createBaseSelector,
  detectBodyModuleTypes,
  getBodyModules,
  image,
  internationalizedArrayField,
  internationalizedStringField,
  pageLanguageFilter,
} from '~/schemas/common'
import useSearchAnalyst, {
  SearchAnalystEventNames,
  SearchAnalystMethods,
} from '~/services/elastic/analyst'
import { useSanity } from '~/services/sanity'
import { useMediaQuery } from '~/services/useMediaQuery'
import bp from '~/styles/breakpoints/breakpoints'
import { currencyCodeToSymbolMap } from '~/utils/currencyMappers'
import { numberWithCommas } from '~/utils/formatCurrency'
import { getArtworkPrice } from '~/utils/getArtworkPrice'
import { addTranslations, type Locale } from '~/utils/i18n'
import { isArtworkInPortfolio } from '~/utils/isArtworkInPortfolio'
import { createLangStaticPaths } from '~/utils/langPaths'
import { resolvePageSeoMeta } from '~/utils/seoHelper'
import { resolveUrlLinksWithTranslation, toURL } from '~/utils/urlResolver'
import { withLocalizedSettings } from '~/utils/withLocalizedSettings'

import type { PDPPageData } from './artist/[slug]/artwork/[artworkSlug]'

import styles from '~/styles/valuation.module.css'

export interface ArtworkLiveValueIndicatorProps {
  item: PDPPageData['heroProps']
  setArtwork: (type?: FormTypes) => void
  isInstantValuation?: boolean
  useContainer?: boolean
  showGoogleReviews?: boolean
  isPDP?: boolean
}

export const ArtworkLiveValueIndicator = ({
  item,
  setArtwork,
  isInstantValuation = true,
  useContainer = true,
  showGoogleReviews = true,
  isPDP = false,
}: ArtworkLiveValueIndicatorProps) => {
  const router = useRouter()
  const { portfolioDetails } = useUserPortfolio()
  const { stateDispatch } = useContext(GlobalStoreContext)
  const { handleFormLaunchFromCtaAnalytics } = useFormAnalytics()
  const { handleValuationButtonClick } = useInstantValuationFormAnalytics()
  const tValuation = useTranslations('valuation')
  const t = useTranslations()
  const {
    listPriceMin,
    listPriceMax,
    poa,
    listPriceMinValues,
    listPriceMaxValues,
    artworkId,
  } = item

  const price = getArtworkPrice(listPriceMin, listPriceMax, poa)
  const { pushEvent } = useMabPostHog()

  const isPOA = price === 'POA'

  const {
    addToMyPortfolioHandler,
    isAddingPortfolio,
    handleViewPortfolioClick,
  } = useAddToPortfolioActions(
    item.artworkId,
    item?.image,
    item?.artist?.id,
    item as any
  )

  const isPortfolioTicket = useMemo(
    () => isArtworkInPortfolio(artworkId, 'portfolio', portfolioDetails),
    [artworkId, portfolioDetails]
  )

  const openSalesForm = useCallback(
    (item: any) => {
      let currentPage = undefined

      let initialValues = {}

      if (isInstantValuation) {
        let ticketId = router.query?.ti || undefined

        // handle when no ticketId is present, search the portfolio for the ticket
        if (ticketId === undefined) {
          const portfolioItem =
            portfolioDetails?.portfolioItems?.portfolio?.find(
              (i: any) =>
                i.artwork_id === parseInt(item.artworkId.split('-')[1])
            )
          // IMPORTANT TODO: Note this reference will need to be updated once new dashboard is live also
          // from id to ticketId as the built items will happen on run time outside of just
          ticketId = portfolioItem?.id || undefined
        }

        currentPage = `${location.protocol}//${location.host}${location.pathname}?artwork=${item.artworkId}${router.query?.ti ? `&ti=${ticketId}` : ''}`

        initialValues = {
          ticket_type: TicketTypes.lookingToSell,
          sale_urgency: 'considering',
          call_to_action_url: currentPage,
          ticketId: ticketId,
        }
      }

      const payload: any = {
        isOpen: true,
        content: FormTypes.sell,
        isDrawer: true,
        props: {
          isReducedWidth: true,
          ...item,
          heroProps: {
            ...item,
            image: item.image,
          },
          title: null,
          formTitle: 'Speak to a specialist',
          portfolioFormType: undefined,
          instantValuationFormType: isInstantValuation,
          hideIntroSlide: true,
          initialValues: {
            ...initialValues,
          },
        },
      }

      handleFormLaunchFromCtaAnalytics(
        'Clicked Request a Specialist Appraisal',
        FormTypes.sell,
        'InstantValuation',
        item.artist,
        item?.artistId,
        item?.title,
        item?.artworkId
      )

      stateDispatch({
        type: GlobalStoreProps.UPDATE_MODAL_STATE,
        payload,
      })
    },
    [
      handleFormLaunchFromCtaAnalytics,
      isInstantValuation,
      portfolioDetails?.portfolioItems?.portfolio,
      router.query?.ti,
      stateDispatch,
    ]
  )

  return (
    <AnimateIntoView type={AnimationType.fadeInFast}>
      <div
        className={classNames(styles.valuationLiveValueContainer, {
          [styles.noBackground]: !isInstantValuation,
          [styles.isStandardValueIndicator]: !isInstantValuation,
        })}
      >
        <Container gutters={!useContainer ? gutterTypes.never : undefined}>
          <AnimateIntoView type={AnimationType.fadeInFromBottom}>
            <div className={styles.valuationLiveValueInner}>
              <div className={styles.valuationLiveValueHeaderContainer}>
                {!isPOA && (
                  <div className={styles.valuationLiveValueHeader}>
                    {!isInstantValuation && <PulsingDot size={DotSize.large} />}
                    <Text
                      size={TextSizes.primaryTitleMed}
                      className={styles.valuationLiveValue}
                    >
                      {isInstantValuation
                        ? tValuation('marketValueIndicator')
                        : tValuation('liveValueIndicator')}
                    </Text>
                  </div>
                )}
                {isPOA && (
                  <div className={styles.valuationLiveValueHeader}>
                    <Text
                      size={TextSizes.primaryTitleMed}
                      className={styles.valuationLiveValue}
                    >
                      {tValuation('specialistAppraisalRequired')}
                    </Text>
                  </div>
                )}
                {(isPOA || !isInstantValuation) && (
                  <Text
                    size={TextSizes.bodyMed}
                    className={classNames(styles.valuationLiveValueText, {
                      [styles.isPOA]: isPOA,
                    })}
                  >
                    {!isPOA
                      ? tValuation('description')
                      : tValuation('instantValueNotAvailable')}
                  </Text>
                )}
              </div>
              {!isPOA && (
                <div className={styles.priceRangeContainer}>
                  <div
                    data-currency="GBP"
                    className={
                      listPriceMinValues &&
                      listPriceMaxValues &&
                      `originalCurrency`
                    }
                  >
                    <Text
                      size={TextSizes.secondaryTitleMed}
                      Tag="p"
                      className={classNames(styles.priceText)}
                    >
                      <span className={styles.priceRange}>{price}</span>
                    </Text>
                  </div>

                  {listPriceMinValues && listPriceMaxValues && !isPOA && (
                    <div className={styles.currencies}>
                      {listPriceMinValues.map((item: any, index: number) => {
                        return (
                          <div
                            data-currency={item.iso}
                            key={item._key}
                            className={styles.currencyItem}
                          >
                            <Text
                              size={TextSizes.secondaryTitleMed}
                              Tag="p"
                              className={styles.priceRange}
                            >
                              {currencyCodeToSymbolMap[item.iso]}
                              {numberWithCommas(item.price)}-
                              {currencyCodeToSymbolMap[item.iso]}
                              {numberWithCommas(
                                listPriceMaxValues[index]?.price
                              )}
                            </Text>
                          </div>
                        )
                      })}
                    </div>
                  )}

                  {isInstantValuation && (
                    <Text
                      size={TextSizes.bodyMed}
                      className={classNames(
                        styles.valuationLiveValueText,
                        styles.marketValueDescription
                      )}
                    >
                      {tValuation('marketValueDescription')}
                    </Text>
                  )}
                </div>
              )}
              {/* <div className={styles.tradingFloorContainer}>
                <TicketLozenges
                  buyTicketsCount={item?.buyTicketsCount}
                  sellTicketsCount={item?.sellTicketsCount}
                  ownedTicketsCount={item?.ownedTicketsCount}
                  size={TicketLozengeSizes.base}
                  inline
                  paddingTop={false}
                  centralize
                  showTradingFloorLogo={false}
                />
              </div> */}

              <div className={styles.requestButtonsContainer}>
                {isInstantValuation && !isPOA ? (
                  <Button
                    variant={ButtonVariants.primaryDark}
                    iconLeft
                    className={styles.requestButton}
                    link="/dashboard/mycollection"
                    linkOnclick={() => {
                      pushEvent('Track in My Collection', {
                        webProduct: PushWebProduct.PDP,
                        interactionLocation: 'Value indicator panel',
                        interactionType: PushInteractionType.Click,
                      })
                    }}
                  >
                    {tValuation('trackInMyCollection')}
                  </Button>
                ) : (
                  <>
                    {isInstantValuation && (
                      <Button
                        variant={ButtonVariants.primaryDark}
                        iconLeft
                        className={styles.requestButton}
                        // launchForm={FormTypes.valuation}
                        button={{
                          type: ButtonTypes.button,
                          onClick: () => {
                            setArtwork()
                            openSalesForm(item)
                            handleValuationButtonClick()
                          },
                        }}
                      >
                        {tValuation('speakToSpecialist')}
                      </Button>
                    )}

                    <Button
                      variant={ButtonVariants.primaryDark}
                      iconLeft
                      className={styles.requestButton}
                      launchForm={FormTypes.valuation}
                      button={{
                        type: ButtonTypes.button,
                        onClick: () => {
                          setArtwork()
                          pushEvent('Request a value indicator valuation', {
                            webProduct: PushWebProduct.PDP,
                            interactionLocation: 'Value indicator panel',
                            interactionType: PushInteractionType.Click,
                          })
                        },
                      }}
                    >
                      {isInstantValuation
                        ? t('dashboard.requestExpertValuation')
                        : tValuation('getAnExpertValuation')}
                    </Button>

                    {isInstantValuation && (
                      <Button
                        variant={ButtonVariants.secondaryDark}
                        className={styles.trackButton}
                        disabled={isAddingPortfolio}
                        button={{
                          type: ButtonTypes.button,
                          onClick: e => {
                            if (!isPortfolioTicket) {
                              addToMyPortfolioHandler()
                              pushEvent('Add to MyPortfolio', {
                                webProduct: PushWebProduct.PDP,
                                interactionLocation: 'Value indicator panel',
                                interactionType: PushInteractionType.Click,
                              })
                            }
                            if (isPortfolioTicket) handleViewPortfolioClick(e)
                          },
                        }}
                      >
                        {isPortfolioTicket
                          ? tValuation('portfolioButtonText.viewIn')
                          : isAddingPortfolio
                            ? tValuation('portfolioButtonText.addingTo')
                            : tValuation('portfolioButtonText.trackIn')}{' '}
                        <em>My</em>
                        Portfolio
                      </Button>
                    )}
                  </>
                )}
              </div>
              {isInstantValuation && (
                <>
                  <div className={styles.tellUsMoreContainer}>
                    <Text
                      size={TextSizes.bodySml}
                      className={styles.tellUsMoreText}
                    >
                      {tValuation.rich('tellUsMore', {
                        link: chunks => (
                          <Button
                            variant={ButtonVariants.noStyles}
                            underline
                            className={styles.tellUsMoreLink}
                            launchForm={FormTypes.valuation}
                            button={{
                              type: ButtonTypes.button,
                              onClick: () => {
                                setArtwork()
                                pushEvent(
                                  'Request a value indicator valuation',
                                  {
                                    webProduct: PushWebProduct.PDP,
                                    interactionLocation: 'Tell us more link',
                                    interactionType: PushInteractionType.Click,
                                  }
                                )
                              },
                            }}
                          >
                            {chunks}
                          </Button>
                        ),
                      })}
                    </Text>
                  </div>

                  <div className={styles.reviewContainer}>
                    {showGoogleReviews && <GoogleInlineReview />}
                    {isInstantValuation && (
                      <Link href="/valuations">
                        <Text
                          size={TextSizes.bodySml}
                          className={styles.valuationLink}
                        >
                          {tValuation('getAnotherValuation')}
                        </Text>
                      </Link>
                    )}
                  </div>
                </>
              )}
            </div>
          </AnimateIntoView>
        </Container>
      </div>
    </AnimateIntoView>
  )
}

export interface ValuationPageData extends PDPPageData {
  auctionHistory: AuctionHistoryItem[]
  artworkSlug: string
  pageBody: any
  lang: Locale
}

const fetcher = (url: string) => fetch(url).then(r => r.json())

export const ValuationPage: NextPage<ValuationPageData> = (
  props: ValuationPageData
) => {
  const { user } = useAuthV2()
  const router = useRouter()
  const isMobile = useMediaQuery(`(max-width: ${bp.bpMedium})`)
  const { handleInstantValuationPageLoaded } =
    useInstantValuationFormAnalytics()
  const { setInitialFormState, handleTicketCreationAnalytics } =
    useFormAnalytics()
  const tValuation = useTranslations('valuation')
  const hasRunInitialAnalytics = useRef(false)
  const hasRunTicketAnalytics = useRef(false)
  const hasFiredZepicEvent = useRef(false)
  const { track: zepicTrack } = useZepic()

  const { pageBody, lang } = props

  const { sendSearchInsightsEvent } = useSearchAnalyst()
  const { state } = useContext(GlobalStoreContext)
  const { currentArtworkState, currentArtworkDispatch } = useContext(
    SelectedArtworkContext
  )

  const { data, error } = useSWR(
    `/api/valuation-data?slug=${currentArtworkState?.selectedArtwork?.id || router.query.artwork}&lang=${lang}`,
    fetcher
  )
  const { artworkId, artist, auctionHistory, valueText, howToBuy } = data ?? {}

  const setCurrentArt = useCallback(
    (payload: any) => {
      currentArtworkDispatch({
        type: CurrentArtworkProps.UPDATE_SELECTED_ARTWORK,
        payload,
      })
    },
    [currentArtworkDispatch]
  )

  const heroProps = useMemo(() => {
    return {
      ...data?.heroProps,
      artworkId: artworkId,
      howToBuy,
      valueText,
      artist: data?.heroProps?.artist?.name,
      artistId: data?.heroProps?.artist?.id,
    } as any
  }, [data, artworkId, howToBuy, valueText])

  useEffect(() => {
    if (!data) return
    if (heroProps?.listPriceMin) {
      const price = getArtworkPrice(
        heroProps.listPriceMin,
        heroProps.listPriceMax,
        heroProps.poa
      )

      handleInstantValuationPageLoaded(price || 'POA')
    }
  }, [heroProps])

  // Run setInitialFormState and handleTicketCreationAnalytics on first render only
  useEffect(() => {
    // Check if we have all required data before running analytics
    if (
      !hasRunInitialAnalytics.current &&
      data &&
      heroProps?.title &&
      heroProps?.artist &&
      heroProps?.artistId &&
      heroProps?.artworkId
    ) {
      console.log('Running initial analytics', {
        title: heroProps.title,
        artist: heroProps.artist,
        artistId: heroProps.artistId,
        artworkId: heroProps.artworkId,
      })

      hasRunInitialAnalytics.current = true

      // Arriving from the valuation journey drawer: keep its formLaunchId and
      // valuation type so the instant journey stays one series in PostHog.
      // Legacy landings carry neither param and mint a fresh id as before
      const { formLaunchId, valuationType } = router.query
      const journeyAnalytics =
        typeof formLaunchId === 'string' && formLaunchId
          ? {
              formLaunchId,
              valuationType: isValuationType(valuationType)
                ? valuationType
                : undefined,
            }
          : undefined

      // Set initial form state with parameters mapped from the artwork data
      setInitialFormState(
        FormTypes.sell, // formType (similar to handleFormLaunchFromCtaAnalytics)
        heroProps.artist, // artistName
        heroProps.artistId, // artistId
        heroProps.title, // artworkTitle
        heroProps.artworkId, // artworkId
        'InstantValuation', // interactionLocation
        journeyAnalytics
      )
    }
  }, [data, heroProps, setInitialFormState, router.query])

  // Run handleTicketCreationAnalytics after form analytics state is populated
  useEffect(() => {
    if (
      !hasRunTicketAnalytics.current &&
      hasRunInitialAnalytics.current &&
      state.formAnalytics?.formLaunchId
    ) {
      console.log('Running ticket creation analytics', state.formAnalytics)
      hasRunTicketAnalytics.current = true

      const ticketId = router.query?.ti ? Number(router.query.ti) : undefined

      handleTicketCreationAnalytics(true, ticketId)
    }
  }, [state.formAnalytics, handleTicketCreationAnalytics])

  // Fire Zepic instant_valuation event once when artwork data is available (skip POA artworks)
  useEffect(() => {
    if (hasFiredZepicEvent.current || !data || !heroProps?.title) return

    const price = getArtworkPrice(
      heroProps.listPriceMin,
      heroProps.listPriceMax,
      heroProps.poa
    )

    if (price === 'POA') return

    hasFiredZepicEvent.current = true

    zepicTrack('instant_valuation', {
      artist_name: heroProps.artist || '',
      artwork_title: heroProps.title || '',
      artwork_image: heroProps.image?.asset
        ? urlFor(heroProps.image.asset, 800)
        : '',
      value_range: price || '',
      format: heroProps.format || '',
      create_date: new Date().toLocaleDateString('en-GB', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      }),
    })
  }, [data, heroProps, zepicTrack])

  const setArtwork = useCallback(
    async (type?: FormTypes) => {
      if (!data) return

      const eventName =
        type === FormTypes.buy
          ? SearchAnalystEventNames.artworkEnquiryToBuyClicked
          : SearchAnalystEventNames.artworkEnquiryToSellClicked

      sendSearchInsightsEvent(SearchAnalystMethods.convertedObjectIDs, {
        eventName,
        indexType: EsIndexTypes.ARTWORKS,
        objectIDs: [artworkId],
      })

      setCurrentArt({
        useInForms: true,
        artist: {
          name: heroProps.artist,
          id: heroProps.artistId,
        },
        selectedArtwork: {
          name: heroProps.title,
          id: artworkId,
          format: heroProps.format || '',
          image: heroProps.image,
        },
      })
    },
    [
      data,
      sendSearchInsightsEvent,
      artworkId,
      setCurrentArt,
      heroProps.artist,
      heroProps.artistId,
      heroProps.title,
      heroProps.format,
      heroProps.image,
    ]
  )

  if (!data && !error) {
    return <Loader />
  }

  if (!data) {
    if (!router.query.artwork) {
      router.push('/valuations')
    }

    return null
  }

  const dimensionsString = getDimensionString(heroProps.dimensions)

  const hasAuctionHistory = Boolean(auctionHistory && auctionHistory.length > 0)

  return (
    <>
      {!user && (
        <AnimateIntoView type={AnimationType.fadeIn}>
          <div className={styles.valuationAuthContainer}>
            <Container>
              <div className={styles.valuationAuthContent}>
                <Text
                  size={TextSizes.primaryTitleMed}
                  Tag="h2"
                  className={styles.valuationAuthTitle}
                >
                  {tValuation('getInstantValuation')}
                </Text>
                <Text
                  size={TextSizes.bodyMed}
                  className={styles.valuationAuthText}
                >
                  {tValuation('seeWorthInSeconds')}
                </Text>
              </div>
              <AuthDialog
                isModal
                omitPrivacy
                headerOptionsOnly
                omitSSOSignup
                phoneNumberOnSignup
                returnToCurrent
                stayOnComplete
              />
            </Container>
          </div>
        </AnimateIntoView>
      )}
      {user && (
        <>
          <AnimateIntoView type={AnimationType.fadeIn}>
            <div className={styles.valuationHeaderContainer}>
              <Container>
                <div className={styles.valuationHeader}>
                  <div className={styles.valuationHeaderLeft}>
                    <PortfolioTableArtwork
                      item={heroProps}
                      hasHistory={false}
                      linkArtwork={false}
                      showTradingFloor={false}
                      size="largeCard"
                    />
                  </div>
                  <div className={styles.valuationHeaderRight}>
                    <div className={styles.valuationSpecDetails}>
                      <ArtworkSpecDetails
                        medium={heroProps?.medium}
                        editionSize={heroProps.editionSize}
                        year={heroProps.year}
                        dimensionsString={dimensionsString}
                        signed={heroProps.signed}
                        format={heroProps.format}
                        useColumns={isMobile}
                      />
                      <div className={styles.valuationLinks}>
                        <Link
                          href={toURL({
                            slug: data?.artworkSlug,
                            artist: heroProps.artistSlug,
                            collection: data.collection?.slug,
                            collectionTaxonomy:
                              data.collection?.collectionTaxonomy,
                            _type: 'artwork',
                          })}
                        >
                          <Text
                            size={TextSizes.bodySml}
                            className={styles.valuationLink}
                          >
                            {tValuation('moreAboutArtwork')}
                          </Text>
                        </Link>
                      </div>
                    </div>
                  </div>
                </div>
              </Container>
            </div>
          </AnimateIntoView>

          <div className={styles.valuationInfoContainer}>
            <ArtworkLiveValueIndicator
              item={heroProps}
              setArtwork={setArtwork}
            />
          </div>
        </>
      )}
      {data && artworkId && (
        <div className={styles.auctionHistoryContainer}>
          <AuctionHistory
            usePostHogAnalytics
            hideTable
            liveMarketOnly={!hasAuctionHistory}
            artworkId={+artworkId.split('-')[1]}
            auctionHistory={auctionHistory ?? []}
            artist={heroProps.artist}
            artwork={heroProps.title}
            buyTicketsCount={heroProps.buyTicketsCount}
            sellTicketsCount={heroProps.sellTicketsCount}
            ownedTicketsCount={heroProps.ownedTicketsCount}
            key={artworkId}
            setArtwork={setArtwork}
            summary={valueText}
          />
        </div>
      )}
      {pageBody?.topBody && (
        <div className={styles.topBody} id="valuation-info">
          {pageBody?.topBody.map((block: any, i: any) =>
            resolveBlock(block, i)
          )}
        </div>
      )}

      {pageBody?.body && (
        <div className={styles.body}>
          {pageBody?.body.map((block: any, i: any) => resolveBlock(block, i))}
        </div>
      )}
    </>
  )
}

export const getValuationArtworkQuery = (
  limiter: any,
  scorer: any = '',
  lang = 'en'
) => {
  return `
  {
    "artwork": *[_type == 'artwork' && (_id == $slug || $slug in _pastSlugs) ${limiter}] ${scorer}[0]{
      "artworkId": _id,
      "heroProps": {
        title,
        "artist": {
          "name": artist->name,
          "id": artist->_id
        },
        "artistSlug": artist->slug.current,
        ${image},
        signed,
        "format": formatDescription->name,
        year,
        editionSize,
        "medium": medium->name,
        condition,
        dimensions,
        poa,
        listPriceMin,
        listPriceMax,
        listPriceMinValues,
        listPriceMaxValues,
        "expert": artist->expert-> {
          "name": name,
          "bio": ${internationalizedArrayField('description', lang)},
          "jobTitle": ${internationalizedStringField('jobTitle')},
          "email": email,
          "image": image,
          "slug": slug.current
        },
        aagr,
        buyTicketsCount,
        sellTicketsCount,
        ownedTicketsCount,
      },
      "artist": {
        "_id": artist->_id,
        "slug": artist->slug.current,
        "about": artist->about[]${resolveUrlLinksWithTranslation(lang)}
      },
      "artworkSlug": slug.current,
      "collection": {
        "_id": collection->_id,
        "title": collection->title,
        "slug": collection->slug.current,
        "collectionTaxonomy": collection->customTaxonomy,
      },
    }
  }`
}

export const getBasicQuery = (
  limiter: any,
  scorer: any = '',
  lang = 'en',
  detectedTypes: string[] = []
) => {
  return `
  {
    "body": *[_type == 'page' && slug.current == $slug && ${pageLanguageFilter(limiter)} ${limiter}] ${scorer}[0] {
      "body": body[] {
        ${getBodyModules(lang, detectedTypes)}
      },
      "topBody": topBody[]${resolveUrlLinksWithTranslation(lang)} {
        ${getBodyModules(lang, detectedTypes)}
      },
      _id,
      meta
    }
  }`
}

const getStaticPropsOriginal = async function getStaticProps(
  context: GetStaticPropsContext,
  localizedData: { settings: any }
): Promise<GetStaticPropsResult<any>> {
  try {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const { client, limiter, scorer } = await useSanity()
    const lang = (context?.params?.lang as Locale) || 'en'

    // STEP 1: Pre-query to detect which body module types are actually used
    const baseSelector = createBaseSelector(
      'page',
      [`slug.current == $slug`, pageLanguageFilter(limiter)],
      limiter,
      scorer
    )
    const detectedTypes = await detectBodyModuleTypes(
      client,
      baseSelector,
      { slug: 'valuation', lang },
      ['body', 'topBody']
    )

    // STEP 2: Main query with only the required body modules
    const data = await client.fetch(
      getBasicQuery(limiter, scorer, lang, detectedTypes),
      {
        slug: 'valuation',
        lang,
      }
    )

    if (data === null) {
      throw new Error('No data found')
    }

    // Use centralized SEO helper with locale-aware templates
    const metaProps = { meta: data.body?.meta, _id: data._id }
    await resolvePageSeoMeta(context, metaProps, 'page')
    data.meta = metaProps.meta

    return {
      props: {
        notFound: Object.keys(data).length < 1,
        title: 'Valuation',
        pageBody: data.body,
        lang: lang,
        meta: {
          ...data.body?.meta,
          title: 'Live Value Indicator | MyArtBroker',
        },
        hasGoogleReviews: true,
        // queryParams: { slug: artworkSlug },
        jsonLd: {},
      },
    }
  } catch (e) {
    console.log('--ERRORss from generating artwork page', e)

    return {
      notFound: true,
    }
  }
}

// Add SSR translation support
export const getStaticProps = withLocalizedSettings(
  async (context, localizedData) => {
    const result = await getStaticPropsOriginal(context, localizedData)
    if ('notFound' in result || 'redirect' in result) {
      return result // Return notFound or redirect directly
    }
    return { props: await addTranslations(result.props, context) }
  }
)
export const getStaticPaths = async () => createLangStaticPaths()

export default ValuationPage
